import React, { useState, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Select,
  MenuItem, 
  FormControl, 
  TextField,
  Backdrop,
  CircularProgress
} from "@mui/material";
import { ValidationError } from '../../utils/ErrorClass';
import { CountCustomerProduct, CustomerProduct } from '../types/customerProduct';
import BackButton from './BackButton';
import ConfirmButton from './ConfirmButton';
import { useCustomer, useShips } from '../hooks';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ja'; // 日本語ロケールをインポート
import { useOrder } from '../hooks/useOrders';
import useVacation from '../hooks/useVaction';
import { routingPath } from '../../utils/path';
import { useAuthContext } from '../auth/AuthContext';
dayjs.locale('ja'); // 日本語ロケールを設定

interface OrderRequest {
  customer_id: number;
  customer_name: string;
  deliver_date: Dayjs;
  ship_id: number;
  ship_address: string;
  ship_name: string;
  comment: string;
  store_close_time: number;
  products: CustomerProduct[];
  tenant_name: string;
}

const validateAndAddOrder = async (orderRequest: OrderRequest, setError: Dispatch<SetStateAction<string>>) => {
  try {
    const response = await fetch(process.env.REACT_APP_AWS_LAMBDA_URL || '', {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': process.env.REACT_APP_AWS_APIGATEWAY_KEY || '',
      },
      mode: 'cors',
      body: JSON.stringify(orderRequest),
    });

    const responseDate = await response.json();

    if (!response.ok) {
      setError(responseDate || 'API呼び出しに失敗しました');
      throw new ValidationError(responseDate || 'API呼び出しに失敗しました');
    }

    return responseDate;
  } catch (error) {
    throw new ValidationError((error as any).message || 'API呼び出しに失敗しました');
  }
};

// 営業日を調整する変数
// 中野さんは27時まで、フーコミさんは25時までの営業時間を設定
const ADDITIONAL_HOURS = process.env.REACT_APP_ENVIRONMENT === 'NAKANO' ? 21 : 23;
const BUSINESS_HOURS = 24 - ADDITIONAL_HOURS;

// 現在時刻に追加時間を加算した日時を取得する関数
function getAdjustedCurrentTime() {
  return dayjs().add(ADDITIONAL_HOURS, 'hour');
}

const getNextAvailableDate = (disabledDates: Dayjs[]) => {
  let date = getAdjustedCurrentTime();
  const maxAttempts = 365;
  let attempts = 0;

  // eslint-disable-next-line no-loop-func
  while (disabledDates.some(disabledDate => date.isSame(disabledDate, 'day'))) {
    date = date.add(1, 'day');
    attempts += 1;
    if (attempts > maxAttempts) {
      throw new Error("No available date found within a year.");
    }
  }
  return date;
};

export default function Cart() {
  const { customer, loading: customerLoading, error: customerError } = useCustomer();
  const { ships, loading: shipmentsLoading, error: shipmentsError } = useShips();
  const [selectedShipIdx, setSelectedShipIdx] = useState<number>(0);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs>(getAdjustedCurrentTime())
  const { order } = useOrder();
  const [error, setError] = useState<string>('');
  const { state, dispatch } = useAuthContext();
  const filteredProducts = state.products.filter((product: CountCustomerProduct) => product.count > 0);
  const [loading, setLoading] = useState<boolean>(false);
  const { vacations, loading: vacationLoading, error: vacationError } = useVacation();
  const navigate = useNavigate();

  const commentRef = useRef<HTMLInputElement>(null);

  const disabledDates = vacations.map(vacation => dayjs(vacation.vacation_date)); // 休暇日を設定
  
  const isDisabledDate = (date: Dayjs) => {
    return date.isBefore(getAdjustedCurrentTime(), 'day') || disabledDates.some(disabledDate => date.isSame(disabledDate, 'day'));
  };

  // 合計金額
  const totalAmount = filteredProducts.reduce((sum, product) => {
    return sum + (product.price * product.count);
  }, 0);

  const handleAddressChange = (event: any) => {
    setSelectedShipIdx(event.target.value as number);
  };

  const handleBackButtonClick = () => {
    navigate(routingPath.auth.orderList);
  };

  const handleDateChange = (newValue: Dayjs | null) => {
    if (commentRef.current) {
      const currentComment = commentRef.current.value;
      // 必要に応じてコメントを処理する
      console.log("Current Comment:", currentComment);
    }
    setDeliveryDate(newValue as Dayjs);
  };

  const handleSubmit = async () => {
    try {
      const remarksValue = commentRef.current?.value || ''; 
      if (!order || !customer) {
        throw new ValidationError('注文情報または顧客情報が不足しています。');
      }
      if (filteredProducts.length === 0) {
        throw new ValidationError('商品を1つ以上カートに入れてください。');
      }

      // 納品希望日が営業時間を過ぎている場合はエラーを表示
      if (deliveryDate.isBefore(getAdjustedCurrentTime(), 'day')) {
        throw new ValidationError('納品希望日が営業時間外です。');
      }

      const orderRequest: OrderRequest = {
        customer_id: customer.id,
        customer_name: customer.name,
        deliver_date: deliveryDate,
        ship_id: ships[selectedShipIdx].id,
        ship_address: ships[selectedShipIdx].address1,
        ship_name: ships[selectedShipIdx].name,
        comment: remarksValue,
        store_close_time: BUSINESS_HOURS,
        products: filteredProducts,
        tenant_name: process.env.REACT_APP_ENVIRONMENT || 'LOCAL',
      };

      setLoading(true);
      await validateAndAddOrder(orderRequest, setError);
      setLoading(false);
      sessionStorage.clear();
      dispatch({ type: 'RESET_PRODUCTS' });
      navigate(routingPath.auth.orderComplete);
    } catch (err) {
      setLoading(false);
      if (err instanceof ValidationError) {
        setError(err.message);
      } else {
        setError('予期せぬエラーが発生しました。');
      }
    }
  };

  useEffect(() => {
    setDeliveryDate(getNextAvailableDate(disabledDates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacations]);

  useEffect(() => {
    const savedComment = sessionStorage.getItem('comment') || '';
    if (savedComment && commentRef.current) {
      commentRef.current.value = savedComment;
    }
  }, []);

  const handleCommentChange = () => {
    if (commentRef.current) {
      sessionStorage.setItem('comment', commentRef.current.value);
    }
  };

  if (customerLoading || shipmentsLoading || vacationLoading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (customerError) {
    return <p>{customerError}</p>;
  }

  if (shipmentsError) {
    return <p>{shipmentsError}</p>;
  }

  if (vacationError) {
    return <p>{vacationError}</p>;
  }

  const CartSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <Paper sx={{ p: 2, mb: 2, boxShadow: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} dateFormats={{ year: 'YYYY年' }} adapterLocale='ja'>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box sx={{ mt: 5, maxWidth: '90%', mx: 'auto' }}>
        <CartSection title='カートの中身'>
          <Grid container spacing={2}>
          {filteredProducts
            .map((product: CountCustomerProduct, index: number) => (
              <Grid item xs={12} key={index}>
                <Paper sx={{ display: "flex", alignItems: "center", p: 2, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">{product.product_name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      ￥{product.price} 数量: {product.count}{product.product_unit}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ textAlign: 'right' }}>
              合計金額: ￥{totalAmount.toLocaleString()}
            </Typography>
          </Box>
        </CartSection>
        
        <FormControl fullWidth>
          <CartSection title='届け先'>
            <Select
              labelId="address-select-label"
              onChange={handleAddressChange}
              value={selectedShipIdx}
              sx={{ fontSize: '0.7rem' }}
            >
              {ships.map((shipment, idx) => (
                <MenuItem key={shipment.id} value={idx} sx={{ fontSize: '0.825rem' }}>
                  {shipment.name}
                </MenuItem>
              ))}
            </Select>
          </CartSection>
          <CartSection title='納品希望日'>
            <DesktopDatePicker
              value={deliveryDate}
              onChange={handleDateChange}
              slotProps={{
                calendarHeader: {
                  format: 'YYYY年MM月',
                },
              }}
              minDate={getNextAvailableDate(disabledDates)}
              format="YYYY/MM/DD"
              shouldDisableDate={isDisabledDate}
              slots={{
                textField: (params) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      readOnly: true,
                    }}
                    sx={{ width: '190px' }}
                  />
                ),
              }}
            />
            <TextField
              label="コメント(任意)"
              inputRef={commentRef}
              onChange={handleCommentChange}
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              margin="normal"
              defaultValue={sessionStorage.getItem('comment') || ''}
            />
          </CartSection>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
          <BackButton 
            onClick={handleBackButtonClick}
          />
          <ConfirmButton 
            onClick={handleSubmit}
          />
        </Box>
        <Box sx={{color: "red"}}>{error}</Box>
      </Box>
    </LocalizationProvider>
  );
};
